import { CookiesKey } from '@starsoft/common/enums';
import {
  useAxiosInstance,
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';
import {
  Coin,
  CoinType,
  GenericError,
  PaginatedResponse,
} from '@starsoft/common/models';
import { useInfiniteQuery } from '@starsoft/common/services/query-client';
import { InfiniteData } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { getCookie } from 'cookies-next';
import { useMemo } from 'react';
import { CoinsQueryKey } from '../../queryKeys';
import { UseCoinsQueryKey } from './props';
import { fetchCoins } from './service';

/**
 * Custom hook to fetch paginated coin data.
 *
 * This hook utilizes the `useInfiniteQuery` from React Query to fetch and manage
 * paginated data for coins. It handles the query key, pagination, and data formatting.
 *
 * @returns {Object} - Returns an object containing the query state and functions.
 * @returns {Coin[]} return.coins - The fetched and formatted paginated coin data.
 * @returns {Object} return.infinityQuery - Contains additional query state and functions.
 */
export function useCoins() {
  const originId = getCookie(CookiesKey.OriginId);
  // Define the query key for caching and refetching purposes
  const queryKey: UseCoinsQueryKey = [
    CoinsQueryKey.Primary,
    {
      limit: 50, // Limit the number of coins per page
      originId,
    },
  ];

  // Get functions to handle pagination
  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();

  // Reset the infinite query state
  useInfiniteQueryReset(queryKey);

  // Get an Axios instance for making HTTP requests
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Use the useInfiniteQuery hook to fetch paginated coin data
  const { data, ...infinityQuery } = useInfiniteQuery<
    PaginatedResponse<Coin>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<Coin>>,
    UseCoinsQueryKey
  >({
    queryKey,
    initialPageParam: 1, // Start fetching from the first page
    queryFn: queryContext => fetchCoins(queryContext, axiosInstance), // Function to fetch data
    getNextPageParam, // Function to get the next page parameter
    getPreviousPageParam, // Function to get the previous page parameter
    staleTime: 5 * 1000, // Data is considered fresh for 5 seconds
  });

  // Memoize the flattened array of coin data from all pages
  const formattedData: Coin[] = useMemo(
    () => data?.pages?.flatMap(page => page?.data),
    [data],
  );

  const cryptoCoins: Coin[] = useMemo(() => {
    if (!formattedData) {
      return [];
    }

    return formattedData.filter(coin => coin.type === CoinType.Crypto);
  }, [formattedData]);

  const fiatCoins: Coin[] = useMemo(() => {
    if (!formattedData) {
      return [];
    }

    return formattedData.filter(coin => coin.type === CoinType.Fiat);
  }, [formattedData]);

  // Return the query state and functions
  return {
    ...infinityQuery,
    cryptoCoins,
    fiatCoins,
    coins: formattedData, // The fetched and formatted paginated coin data
  };
}
